import { supabase } from './supabase'

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// fetch con Authorization: Bearer <JWT Supabase>. Úsalo para llamar a endpoints
// protegidos con requireAuth en el backend (ej. /api/generate-copy).
// `timeoutMs` cancela la request si tarda demasiado (default: sin timeout).
// `signal` permite al caller cancelar manualmente (ej. cleanup de useEffect).
export async function authFetch(path, { method = 'GET', body, headers, timeoutMs, signal } = {}) {
  const { data } = await supabase.auth.getSession()
  let token = data?.session?.access_token
  if (!token) {
    // Auto-recuperación: si no hay token (sesión caducada), intentamos
    // refrescarla en silencio ANTES de rendirnos. Así un usuario real no
    // tiene que recargar ni reloguear — la sesión se renueva sola.
    try {
      const { data: refreshed } = await supabase.auth.refreshSession()
      token = refreshed?.session?.access_token
    } catch { /* no hay sesión recuperable */ }
  }
  if (!token) throw new Error('auth_required')

  // Combinar signal del caller con timeout propio si ambos están.
  let finalSignal = signal
  let killer = null
  if (timeoutMs) {
    const ctl = new AbortController()
    killer = setTimeout(() => ctl.abort(), timeoutMs)
    if (signal) {
      // Propagar abort del signal del caller al interno
      signal.addEventListener?.('abort', () => ctl.abort(), { once: true })
    }
    finalSignal = ctl.signal
  }

  try {
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData
    const baseHeaders = isForm
      ? { Authorization: `Bearer ${token}` }
      : { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    const res = await fetch(`${API}${path}`, {
      method,
      headers: { ...baseHeaders, ...(headers || {}) },
      body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
      signal: finalSignal,
    })
    return res
  } finally {
    if (killer) clearTimeout(killer)
  }
}

// Wrapper que parsea JSON y lanza si no-ok. El error incluye .status para
// que los callers puedan distinguir 401 (auth) vs 429 (rate) vs 500 (server).
export async function authFetchJson(path, opts) {
  const res = await authFetch(path, opts)
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `HTTP ${res.status}`)
    err.status = res.status
    err.data = data
    throw err
  }
  return data
}

export { API }
